'use client';

import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@/context/AuthContext/AuthContext';

interface UseCommunityGuidelinesResult {
  saving: boolean;
  error: string | null;
  hasAccepted: boolean;
  accept: () => Promise<boolean>;
  clearError: () => void;
}

/**
 * Wraps the POST /api/users/me/accept-community-guidelines call (MWBI-668).
 *
 * Records acceptance server-side, then optimistically flips local auth state
 * so any UI bound to `hasAccepted` updates immediately. `CommunityGuidelinesGate`
 * drives the blocking modal off `hasAccepted`; this hook is the single place
 * acceptance is recorded.
 */
export function useCommunityGuidelines(): UseCommunityGuidelinesResult {
  const { currentUser, refreshCurrentUser, patchCurrentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Synchronous in-flight guard: `disabled={saving}` only takes effect after a
  // re-render, so a fast double-click could otherwise fire two POSTs.
  const inFlight = useRef(false);

  const accept = useCallback(async (): Promise<boolean> => {
    if (inFlight.current) return false;
    inFlight.current = true;
    setSaving(true);
    setError(null);

    try {
      const res = await fetch('/api/strapi/users/me/accept-community-guidelines', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!res.ok) {
        setError('save_failed');
        return false;
      }

      // Optimistically flip local state from the POST response so any UI
      // bound to hasAccepted updates immediately — even if the background
      // /api/auth/me refresh below fails.
      try {
        const data = await res.json() as { acceptedCommunityGuidelinesAt?: string };
        const acceptedAt = data?.acceptedCommunityGuidelinesAt ?? new Date().toISOString();
        patchCurrentUser({ acceptedCommunityGuidelinesAt: acceptedAt });
      } catch (parseErr) {
        // POST already succeeded; body parse failure is non-fatal — fall back
        // to a client stamp so any UI bound to hasAccepted still flips.
        console.warn('accept response body parse failed; using client timestamp', parseErr);
        patchCurrentUser({ acceptedCommunityGuidelinesAt: new Date().toISOString() });
      }

      // Fire-and-forget full hydration so other fields stay fresh; the user
      // is already considered accepted at this point.
      void refreshCurrentUser();
      return true;
    } catch (err) {
      console.error('Failed to accept community guidelines:', err);
      setError('save_failed');
      return false;
    } finally {
      setSaving(false);
      inFlight.current = false;
    }
  }, [refreshCurrentUser, patchCurrentUser]);

  return {
    saving,
    error,
    hasAccepted: Boolean(currentUser?.acceptedCommunityGuidelinesAt),
    accept,
    clearError: () => setError(null),
  };
}
